import { ref, computed } from 'vue';

// Shared state to persist timer across component remounts
const RESEND_DELAY_SECONDS = 30;

// Store the timestamp when the next resend is allowed (persisted across component lifecycle)
const resendAllowedAt = ref<number | null>(null);
const currentCountdown = ref<number>(0);
let intervalId: ReturnType<typeof setInterval> | null = null;
let rateLimitedUntil = 0;

export const isPhoneRateLimited = () => rateLimitedUntil > Date.now();

export function applyPhoneRateLimit(retryAfter?: unknown) {
  const seconds = Number(retryAfter);
  const dateSeconds = typeof retryAfter === 'string'
    ? Math.ceil((Date.parse(retryAfter) - Date.now()) / 1000) : NaN;
  const delay = Number.isFinite(seconds) && seconds > 0 ? seconds
    : Number.isFinite(dateSeconds) && dateSeconds > 0 ? dateSeconds : 180;
  rateLimitedUntil = Math.max(rateLimitedUntil, Date.now() + delay * 1000);
  resendAllowedAt.value = Math.max(resendAllowedAt.value || 0, rateLimitedUntil);
  ensureIntervalRunning();
  updateCountdown();
}

function updateCountdown() {
  if (resendAllowedAt.value === null) {
    currentCountdown.value = 0;
    return;
  }

  const now = Date.now();
  const remaining = Math.ceil((resendAllowedAt.value - now) / 1000);
  currentCountdown.value = Math.max(0, remaining);

  // Clear interval when countdown reaches 0
  if (currentCountdown.value === 0 && intervalId) {
    clearInterval(intervalId);
    intervalId = null;
    resendAllowedAt.value = null;
  }
}

function ensureIntervalRunning() {
  if (intervalId === null && resendAllowedAt.value !== null) {
    updateCountdown();
    intervalId = setInterval(updateCountdown, 1000);
  }
}

/**
 * Composable to manage resend code timer with persistence across component remounts.
 * Prevents users from retrying too early by maintaining a countdown timer.
 *
 * @param initiallyStartTimer - If true, starts the timer immediately on first use
 */
export function useResendTimer(initiallyStartTimer = false) {
  const isResending = ref(false);

  // Start timer if requested and not already running
  if (initiallyStartTimer && resendAllowedAt.value === null) {
    resendAllowedAt.value = Date.now() + RESEND_DELAY_SECONDS * 1000;
    ensureIntervalRunning();
  } else {
    // Ensure interval is running if there's an active countdown
    ensureIntervalRunning();
  }

  const canResend = computed(() => {
    return currentCountdown.value === 0 && !isResending.value;
  });

  const timerBeforeResend = computed(() => currentCountdown.value);

  /**
   * Starts a new countdown timer after a resend action.
   * Call this after a successful resend API call.
   */
  function startTimer() {
    resendAllowedAt.value = Math.max(rateLimitedUntil, Date.now() + RESEND_DELAY_SECONDS * 1000);
    ensureIntervalRunning();
  }

  /**
   * Resets the timer state completely.
   * Call this when the phone verification is successful or the flow is cancelled.
   */
  function resetTimer() {
    rateLimitedUntil = 0;
    resendAllowedAt.value = null;
    currentCountdown.value = 0;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  /**
   * Wrapper for resend code actions that handles loading state and timer restart.
   * Prevents double-clicks and ensures timer restarts after successful resend.
   *
   * @param resendFn - The async function to call to resend the code
   * @returns Promise that resolves when the resend is complete
   */
  async function handleResend(resendFn: () => Promise<void>) {
    if (!canResend.value) {
      return;
    }

    isResending.value = true;
    try {
      await resendFn();
      startTimer();
    } finally {
      isResending.value = false;
    }
  }

  return {
    timerBeforeResend,
    canResend,
    isResending,
    startTimer,
    resetTimer,
    handleResend,
    RESEND_DELAY_SECONDS,
  };
}
